import {Injectable} from '@angular/core';
// rxjs
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import {tap, map, filter} from 'rxjs/operators';
// services
import {RestaurantService} from '../services/restaurant.service';
import {RouteService} from '../services/route.service';
// models
import {
  ModifierGroup,
  Restaurant,
  RestaurantAvailableStatus,
  RestaurantCategory,
  RestaurantProduct,
  Taxes, CatalogData, VariationInventoryInfo, Country, Group, CustomCss, TimeRange, RangeParam, ItemFilter, ProductVariation,
  RewardProduct
} from '../models/restaurant.model';
import {FormatDataService} from '../services/format-data.service';
import {FormatStyleService} from '../services/format-style.service';
import {ActivatedRoute, Router} from '@angular/router';
import {PaymentStore} from './payment.store';
import { UtilService } from '../services/util.service';
import { StorageService } from '../services/storage.service';
import { UrlParam } from '../enums/group-param.enum';
import { KioskSmsOrder } from '../models/payment-item.model';

@Injectable()
export class RestaurantStore {
  // restaurant's state properties
  private _restaurants$ = new BehaviorSubject<Restaurant[]>([]);
  private _restaurantAvailableStatus$ = new BehaviorSubject<RestaurantAvailableStatus>(new RestaurantAvailableStatus());
  private _estimateDeliveryTime$ = new BehaviorSubject<number>(0);
  private _group$ = new BehaviorSubject<Group>(null);
  private _isGroup$ = new BehaviorSubject<boolean>(false);
  private _curCategories$ = new BehaviorSubject<RestaurantCategory[]>([]);
  private _curProducts$ = new BehaviorSubject<RestaurantProduct[][]>([]);
  private _restaurantModifiers$ = new BehaviorSubject<ModifierGroup[]>([]);
  private _restaurantCountryInfo$ = new BehaviorSubject<Country>(new Country());
  private _taxes$ = new BehaviorSubject<Taxes[]>([]);
  private _filteredWorkHours$ = new BehaviorSubject<any>(null);
  private _isDefaultTax$ = new BehaviorSubject<boolean>(false);
  private _restaurantIsAvailable$ = new BehaviorSubject<boolean>(true);
  private _hideRestaurantOrdersAhead$ = new BehaviorSubject<boolean>(false);
  private _openItemCardInProgress$ = new BehaviorSubject<boolean>(false);
  private _loadItemsInProgress$ = new BehaviorSubject<boolean>(false);
  private _categoryLoadedQueue$ = new BehaviorSubject<RestaurantCategory[]>([]);
  private _isCatalogUploaded$ = new BehaviorSubject<boolean>(false);
  private _timezone = new BehaviorSubject<string>(null);
  private _enabledFulfillments = new BehaviorSubject<string[]>([]);
  private _fulfillmentParams = new BehaviorSubject<string[]>([]);
  private _timeRangeParams = new BehaviorSubject<TimeRange>(null);
  private _discountPromo = new BehaviorSubject<string>(null);
  private _subscriptionCode = new BehaviorSubject<string>(null);
  private _updateSubscriptionHistory$ = new BehaviorSubject<boolean>(false);
  private _disableTodayOrders$ = new BehaviorSubject<boolean>(false);
  private _activeLocationIndex$ = new BehaviorSubject<number>(0);
  private _itemColumns$ = new BehaviorSubject<number>(null);
  private _updateGroupInfo$ = new BehaviorSubject<boolean>(false);
  private _updatePhoneNumber$ = new BehaviorSubject<boolean>(false);
  private _loyaltyPointsName$ = new BehaviorSubject<any>(null);
  private _restaurantChanged$ = new BehaviorSubject<any>(false);
  private _pickupLocationChanges$ = new BehaviorSubject<any>(null);
  private _selectedLocationInGroup = new BehaviorSubject<string>(null);
  private _groupIdForRedirect = new BehaviorSubject<string>(null);
  private _isGoToCheckout = new BehaviorSubject<boolean>(false);
  private _isRestaurantsLoaded: Subject<boolean> = new Subject<boolean>();
  private _isKioskSmsOrder = new BehaviorSubject<boolean>(false);
  private _kioskSmsOrderData = new BehaviorSubject<KioskSmsOrder>(null);
  private _catalogIsEmpty: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private _rewardProducts: BehaviorSubject<RewardProduct[]> = new BehaviorSubject<RewardProduct[]>([]);

  public get isDefaultTax$(): Observable<boolean> {
    return this._isDefaultTax$.asObservable();
  }

  public get restaurants$(): Observable<Restaurant[]> {
    return this._restaurants$.asObservable();
  }

  public get group$(): Observable<Group> {
    return this._group$.asObservable();
  }

  public get group(): Group {
    return this._group$.getValue();
  }

  public get catalogIsEmpty$(): Observable<boolean> {
    return this._catalogIsEmpty.asObservable();
  }

  public set catalogIsEmpty(isEmpty: boolean)  {
    this._catalogIsEmpty.next(isEmpty);
  }

  public set group(value: Group) {
    this._group$.next(value);
  }

  public get updateGroupInfo$(): Observable<boolean> {
    return this._updateGroupInfo$.asObservable();
  }

  public set updateGroupInfo(value: boolean) {
    this._updateGroupInfo$.next(value);
  }

  public get updatePhoneNumber$(): Observable<boolean> {
    return this._updatePhoneNumber$.asObservable();
  }

  public set updatePhoneNumber(value: boolean) {
    this._updatePhoneNumber$.next(value);
  }

  public get sortedRestaurants$(): Observable<Restaurant[]> {
    return new BehaviorSubject<Restaurant[]>(this._restaurants$.getValue()).asObservable();
  }

  public get restaurantAvailableStatus$(): Observable<RestaurantAvailableStatus> {
    return this._restaurantAvailableStatus$.asObservable();
  }

  public get curCategories$(): Observable<RestaurantCategory[]> {
    return this._curCategories$.asObservable();
  }

  public get curProducts$(): Observable<RestaurantProduct[][]> {
    return this._curProducts$.asObservable();
  }

  public get taxes$(): Observable<Taxes[]> {
    return this._taxes$.asObservable();
  }

  public get filteredWorkHours$(): Observable<any> {
    return this._filteredWorkHours$.asObservable();
  }

  public get filteredWorkHours(): any {
    return this._filteredWorkHours$.getValue();
  }

  public get restaurantIsAvailable$(): Observable<boolean> {
    return this._restaurantIsAvailable$.asObservable();
  }

  public get hideRestaurantOrdersAhead(): boolean {
    return this._hideRestaurantOrdersAhead$.getValue();
  }

  public get updateSubscriptionHistory$(): Observable<boolean> {
    return this._updateSubscriptionHistory$.asObservable();
  }

  public get updateSubscriptionHistory(): boolean {
    return this._updateSubscriptionHistory$.getValue();
  }

  public get disableTodayOrders(): boolean {
    return this._disableTodayOrders$.getValue();
  }

  public get itemColumns(): number {
    return this._itemColumns$.getValue();
  }

  public get openItemCardInProgress(): boolean {
    return this._openItemCardInProgress$.getValue();
  }

  public get curCategories(): RestaurantCategory[] {
    return this._curCategories$.getValue();
  }

  public get curProducts(): RestaurantProduct[][] {
    return this._curProducts$.getValue();
  }

  public set curProducts(value: RestaurantProduct[][]) {
    this._curProducts$.next(value);
  }

  public get categoryLoadedQueue(): RestaurantCategory[] {
    return this._categoryLoadedQueue$.getValue();
  }

  public set categoryLoadedQueue(value: RestaurantCategory[]) {
    this._categoryLoadedQueue$.next(value);
  }

  public get restaurantModifiers(): ModifierGroup[] {
    return this._restaurantModifiers$.getValue();
  }

  public get restaurantCountryInfo(): Country {
    return this._restaurantCountryInfo$.getValue();
  }

  public set restaurantCountryInfo(value: Country) {
    this._restaurantCountryInfo$.next(value);
  }

  public get loadItemsInProgress(): boolean {
    return this._loadItemsInProgress$.value;
  }

  public set loadItemsInProgress(value: boolean) {
    this._loadItemsInProgress$.next(value);
  }

  public get isCatalogUploaded(): boolean {
    return this._isCatalogUploaded$.value;
  }

  public set isCatalogUploaded(value: boolean) {
    this._isCatalogUploaded$.next(value);
  }

  public get isGroup(): boolean {
    return this._isGroup$.value;
  }

  public set isGroup(value: boolean) {
    this._isGroup$.next(value);
  }

  public get loyaltyPointsName(): any {
    return this._loyaltyPointsName$.value;
  }

  public set loyaltyPointsName(value: any) {
    this._loyaltyPointsName$.next(value);
  }

  public get activeLocationIndex(): number {
    return this._activeLocationIndex$.getValue();
  }

  public set activeLocationIndex(value: number) {
    this._activeLocationIndex$.next(value);
  }

  get activeLocationIndex$(): Observable<number> {
    return this._activeLocationIndex$.asObservable();
  }

  public get estimateDeliveryTime(): number {
    return this._estimateDeliveryTime$.getValue();
  }

  public set estimateDeliveryTime(value: number) {
    this._estimateDeliveryTime$.next(value);
  }

  public get timezone(): string {
    return this._timezone.value;
  }

  public set timezone(value: string) {
    this._timezone.next(value);
  }

  public get enabledFulfillments(): string[] {
    return this._enabledFulfillments.value;
  }

  public set enabledFulfillments(value: string[]) {
    this._enabledFulfillments.next(value);
  }

  public get fulfillmentParams(): string[] {
    return this._fulfillmentParams.value;
  }

  public set fulfillmentParams(value: string[]) {
    this._fulfillmentParams.next(value);
  }

  public get timeRangeParams(): TimeRange {
    return this._timeRangeParams.value;
  }

  public set timeRangeParams(value: TimeRange) {
    this._timeRangeParams.next(value);
  }

  public get discountPromo(): string {
    return this._discountPromo.value;
  }

  public set discountPromo(value: string) {
    this._discountPromo.next(value);
  }

  public get subscriptionCode(): string {
    return this._subscriptionCode.value;
  }

  public set subscriptionCode(value: string) {
    this._subscriptionCode.next(value);
  }

  public set itemColumns(value: number) {
    this._itemColumns$.next(value);
  }


  public set restaurantChanged(locationId:string){
    this._restaurantChanged$.next(locationId);
  }

  public get restaurantChanged():string {
    return this._restaurantChanged$.value;
  }

  public set pickupLocationChanged(locationId: any){
     this._pickupLocationChanges$.next(locationId);
  }

  public get pickupLocationChanged(): any {
    return this._pickupLocationChanges$.asObservable();
  }

  public get selectedLocationInGroup(): string {
    return this._selectedLocationInGroup.value;
  }

  public set selectedLocationInGroup(value: string) {
    this._selectedLocationInGroup.next(value);
  }

  public get groupIdForRedirect() {
    return this._groupIdForRedirect.value;
  }

  public set groupIdForRedirect(value: string) {
    this._groupIdForRedirect.next(value);
  }

  public get isGoToCheckout() {
    return  this._isGoToCheckout.value;
  }

  public set isGoToCheckout(value: boolean) {
    this._isGoToCheckout.next(value);
  }
  public getIsRestaurantLoaded(): Observable<boolean> {
    return this._isRestaurantsLoaded.asObservable();
  }
  public setIsRestaurantLoaded(value: boolean): void {
    this._isRestaurantsLoaded.next(value);
  }

  public get isKioskSmsOrder(): boolean {
    return this._isKioskSmsOrder.value;
  }

  public set isKioskSmsOrder(isKioskSmsOrder: boolean) {
    this._isKioskSmsOrder.next(isKioskSmsOrder);
  }

  public get kioskSmsOrderData(): KioskSmsOrder {
    return this._kioskSmsOrderData.getValue();
  }

  public set kioskSmsOrderData(value: KioskSmsOrder) {
    this._kioskSmsOrderData.next(value);
  }

  get rewardProducts(): any[] {
    return this._rewardProducts.value;
  }
  
  set rewardProducts(products: any[]) {
    this._rewardProducts.next(products);
  }

  // private productsCache
  private categoriesCache = {};
  private taxesCache = {};
  private modifiersCache = {};

  constructor(
    private paymentStore: PaymentStore,
    private formatStyleService: FormatStyleService,
    private formatDataService: FormatDataService,
    private restaurantService: RestaurantService,
    private routeService: RouteService,
    private router: Router,
    private route: ActivatedRoute,
    private utilService: UtilService,
    private storageService: StorageService,
  ) {}

  public bootstrapRestaurants(id: string, isGroup?: boolean): void {
    const url = `merchants/${id}`;
    let baseUrl = url.split('?')[0];
    if (isGroup) {
      baseUrl = `groups/${id}`;
    }
    if (this.isKioskSmsOrder) {
      baseUrl = `merchants/${id}`;
    }
    if (baseUrl && this._restaurants$.getValue().length === 0) {
      this.restaurantService.getRestaurants(baseUrl)
        .subscribe(async(restaurants: Restaurant[]) => {
          this._restaurants$.next(restaurants);
          this.setCustomCss(restaurants[0]?.customCSS);
          this.setCustomStyleService(restaurants[0])
          const filteredWorkHours = {};
          for (const restaurant of restaurants) {
            filteredWorkHours[restaurant.locationId] = this.formatDataService.formatWorkHoursWithBlackout(restaurant.hoursOfOperation, restaurant.blackoutDateRanges, new Date());
          }
          this._filteredWorkHours$.next(filteredWorkHours);
          this.setIsRestaurantLoaded(true);
        });
      return;
    }
    this.setIsRestaurantLoaded(true);
  }

  public updateWorkHours(id: string, isGroup?: boolean): void {
    let baseUrl = `merchants/${id}`;
    if (isGroup) {
      baseUrl = `groups/${id}`;
    }
    if (baseUrl) {
      this.restaurantService.getRestaurants(baseUrl)
        .subscribe(async(restaurants: Restaurant[]) => {
          const filteredWorkHours = {};
          restaurants.forEach((restaurant: Restaurant) => {
            filteredWorkHours[restaurant.locationId] = this.formatDataService.formatWorkHoursWithBlackout(restaurant.hoursOfOperation, restaurant.blackoutDateRanges, new Date());
          });
          this._filteredWorkHours$.next(filteredWorkHours);
        });
    }
  }

  public getRestaurantById(id: string): Observable<Restaurant> {
    return this.restaurants$.pipe(
      map(restaurants => restaurants.find(item => item.locationId === id))
    );
  }

  public getRestaurants(): Observable<Restaurant[]> {
    return this.restaurants$;
  }

  public getGroupById(groupId: string): Observable<Group> {
    const baseUrl = groupId ? `groups/${groupId}` : this.routeService.getBaseUrl();
    return this.restaurantService.getGroup(baseUrl);
  }

  public clearCurCategoriesAndCurProducts(): void {
    this._curCategories$.next([]);
    this._curProducts$.next([]);
  }
  
  public clearCurProducts(): void {
    this._curProducts$.next([]);
  }

  updateCategories(newCategories: any[]) {
    this._curCategories$.next(newCategories);
  }

  private isCorrectTax(taxes: Taxes[]): boolean {
    if (!taxes) return false;

    if (taxes instanceof Array && taxes.length) {
      for (const tax of taxes) {
        if (!this.isValidTaxField(tax)) return false;
      }
    } else return false;

    return true;
  }

  private isValidTaxField(tax) {
    for (const taxesKey in tax) {
      if (!tax[taxesKey]) return false;
    }
  }

  private setDefaultTaxValue(taxes: Taxes[] | null, restaurantId: string): Taxes[] {
    taxes.forEach(tax => {
      // tslint:disable-next-line:forin
      for (const taxKey in tax) {
        if (taxKey === 'id') {
          tax[taxKey] = tax[taxKey] || restaurantId;
        }
          tax[taxKey] = tax[taxKey] || 0;
      }
    });
    return taxes;
  }

  public setRestaurantIsAvailable(value: boolean) {
    this._restaurantIsAvailable$.next(value);
  }

  public set updateSubscriptionHistory(value: boolean) {
    this._updateSubscriptionHistory$.next(value);
  }

  public setRestaurantAvailableStatus(value: RestaurantAvailableStatus) {
    this._restaurantAvailableStatus$.next(value);
  }

  public set hideRestaurantOrdersAhead(value: boolean) {
    this._hideRestaurantOrdersAhead$.next(value);
  }

  public set disableTodayOrders(value: boolean) {
    this._disableTodayOrders$.next(value);
  }

  public set openItemCardInProgress(value: boolean) {
    this._openItemCardInProgress$.next(value);
  }

  public checkRouteContainsTimeRangeOptions(): void {
    if (this.route.snapshot.queryParams.startTime != null && this.route.snapshot.queryParams.startTime.length > 0 &&
        this.route.snapshot.queryParams.endTime != null && this.route.snapshot.queryParams.endTime.length > 0)  {
      const timeRange = new TimeRange(new Date(this.route.snapshot.queryParams.startTime), new Date(this.route.snapshot.queryParams.endTime));
      sessionStorage.setItem(RangeParam.RANGE_START_TIME, timeRange.startTime.toISOString());
      sessionStorage.setItem(RangeParam.RANGE_END_TIME, timeRange.endTime.toISOString());
      this.timeRangeParams = timeRange;
    } else if (sessionStorage.getItem(RangeParam.RANGE_START_TIME) != null && sessionStorage.getItem(RangeParam.RANGE_START_TIME).length > 0 &&
               sessionStorage.getItem(RangeParam.RANGE_END_TIME) != null && sessionStorage.getItem(RangeParam.RANGE_END_TIME).length > 0) {
      this.timeRangeParams = new TimeRange(new Date(sessionStorage.getItem(RangeParam.RANGE_START_TIME)), new Date(sessionStorage.getItem(RangeParam.RANGE_END_TIME)));
    }
  }

  public checkRouteContainsDiscountOption(): void {
    if (this.route.snapshot.queryParams.discount != null && this.route.snapshot.queryParams.discount.length > 0)  {
      sessionStorage.setItem(UrlParam.discount, this.route.snapshot.queryParams.discount);
      this.discountPromo = this.route.snapshot.queryParams.discount;
    } else if (sessionStorage.getItem(UrlParam.discount) != null && sessionStorage.getItem(UrlParam.discount).length > 0) {
      this.discountPromo = sessionStorage.getItem(UrlParam.discount);
    }
  }

  public checkCustomNumber(): void {
    if (this.route.snapshot.queryParams.customInstruction) {
      sessionStorage.setItem(UrlParam.customInstruction, decodeURIComponent(this.route.snapshot.queryParams.customInstruction));
    }
  }

  public checkRouteContainsFullfillmentOptions(): void {
    if (this.route.snapshot.queryParams.fulfillments != null && this.route.snapshot.queryParams.fulfillments.length > 0) {
      sessionStorage.setItem(UrlParam.fulfillments, this.route.snapshot.queryParams.fulfillments);
      this.fulfillmentParams = this.route.snapshot.queryParams.fulfillments.split(',');
    } else if (sessionStorage.getItem(UrlParam.fulfillments) != null && sessionStorage.getItem(UrlParam.fulfillments).length > 0) {
      this.fulfillmentParams = sessionStorage.getItem(UrlParam.fulfillments).split(',');
    }
  }

  public checkRouteContainsTableOptions(): void {
    if (this.route.snapshot.queryParams.tableNumber != null && this.route.snapshot.queryParams.tableNumber.length > 0) {
      sessionStorage.setItem(UrlParam.tableNumber, this.route.snapshot.queryParams.tableNumber);
    }
  }

  public checkRouteContainsSubscriptionOptions(): void {
    if (this.route.snapshot.queryParams.subscription != null && this.route.snapshot.queryParams.subscription.length > 0)  {
      sessionStorage.setItem(UrlParam.subscription, this.route.snapshot.queryParams.subscription);
      this.subscriptionCode = this.route.snapshot.queryParams.subscription;
    } else if (sessionStorage.getItem(UrlParam.subscription) != null && sessionStorage.getItem(UrlParam.subscription).length > 0) {
      this.subscriptionCode = sessionStorage.getItem(UrlParam.subscription);
    }
  }

  public checkRouteContainsTableNumber(tableNumber: string): void {
    this.checkRouteContainsTableOptions();
    if (tableNumber && !this.paymentStore.isLinkTableNumber) {
      this.fulfillmentParams = ['tableside', 'tabs'];
      this.paymentStore.tableNumber = tableNumber;
      this.paymentStore.isLinkTableNumber = !!tableNumber;
      sessionStorage.setItem(UrlParam.tableNumber, tableNumber);
    } else if (sessionStorage.getItem(UrlParam.tableNumber) != null && sessionStorage.getItem(UrlParam.tableNumber).length > 0) {
      this.fulfillmentParams = ['tableside', 'tabs'];
      this.paymentStore.tableNumber = sessionStorage.getItem(UrlParam.tableNumber);
      this.paymentStore.isLinkTableNumber = !!sessionStorage.getItem(UrlParam.tableNumber);
    }
  }

  public checkRoutQueryParams(): void {
    this.route.queryParams.subscribe(params => {
      if (params && params.tableNumber) {
        this.checkRouteContainsTableNumber(params.tableNumber);
      }
    });
  }

  public getRestaurantCatalogData(locationId: string, isCatalogUploaded: boolean, isCategoriesCollapsable: boolean, merchantId: string): Observable<any> {
    const locallyStoredCategories: RestaurantCategory[] = this.categoriesCache[locationId];
    const locallyStoredTaxes: Taxes[] = this.taxesCache[locationId];
    const locallyStoredModifiers: ModifierGroup[] = this.modifiersCache[locationId];
    if (locallyStoredCategories && locallyStoredTaxes && locallyStoredModifiers) {
      this._curCategories$.next(locallyStoredCategories);
      this._taxes$.next(locallyStoredTaxes);
      this._restaurantModifiers$.next(locallyStoredModifiers);
      return of(new CatalogData(locallyStoredCategories, locallyStoredTaxes, locallyStoredModifiers));
    } else {
      return this.restaurantService.getRestaurantCatalogData(locationId, isCatalogUploaded, merchantId).pipe(
        tap((result: CatalogData) => {
          this.catalogIsEmpty = result.categories?.length === 0;
          if (isCategoriesCollapsable) {
            result.categories.forEach((category, index) => {
                category.hideItems = true;
            });
          }
          this._curCategories$.next(result.categories);
          this.categoriesCache[locationId] = result.categories;

          if (!this.isCorrectTax(result.taxes)) {
            result.taxes = this.setDefaultTaxValue(result.taxes, locationId);
            this._isDefaultTax$.next(true);
          }
          this._taxes$.next(result.taxes);
          this.taxesCache[locationId] = result.taxes;

          this._restaurantModifiers$.next(result.modifiers);
          this.modifiersCache[locationId] = result.modifiers;

          return result;
        })
      );
    }
  }

  public getGroupCatalogData(locationId: string, isCatalogUploaded: boolean, merchantId?: string): Observable<any> {
    return this.restaurantService.getRestaurantCatalogData(locationId, isCatalogUploaded, merchantId)
      .pipe(
        tap((result: CatalogData) => {
          this._curCategories$.next(this._curCategories$.getValue().concat(result.categories));
          if (!this.isCorrectTax(result.taxes)) {
            result.taxes = this.setDefaultTaxValue(result.taxes, locationId);
            this._isDefaultTax$.next(true);
          }
          this._taxes$.next(this._taxes$.getValue().concat(result.taxes));
          this._restaurantModifiers$.next(this._restaurantModifiers$.getValue().concat(result.modifiers));

          return result;
        })
      );
  }

  public getProductsByCategoryId(locationId: string, isCatalogUploaded: boolean, isEmptyProductsList: boolean, category: RestaurantCategory, merchantId?: string): Observable<RestaurantProduct[]> {
    const modifiers = this.restaurantModifiers;

    if (isEmptyProductsList) {
      this._curProducts$.next([]);
    }

    return this.restaurantService.getCategoryProducts(locationId, category.id, modifiers, isCatalogUploaded, category, merchantId)
      .pipe(filter(Boolean), tap((restaurantProducts: RestaurantProduct[]) => {
        this._curProducts$.next(this._curProducts$.getValue().concat([restaurantProducts]));
      }));
  }

  public getAllProductsByLocationId(locationId: string, isCatalogUploaded: boolean, isEmptyProductsList: boolean, merchantId: string): Observable<RestaurantProduct[]> {
    const modifiers = this.restaurantModifiers;

    if (isEmptyProductsList) {
      this._curProducts$.next([]);
    }

    return this.restaurantService.getAllCategoryProducts(locationId, modifiers, isCatalogUploaded, merchantId)
      .pipe(filter(Boolean), tap((restaurantProducts: RestaurantProduct[]) => {
        this._curProducts$.next(this._curProducts$.getValue().concat([restaurantProducts]));
      }));
  }

  public getProductsInventory(locationId: string, variationIds: string[], merchantId?: string): Observable<any> {
    return this.restaurantService.getRestaurantInventoryData(locationId, variationIds, merchantId)
      .pipe(tap((variationList: VariationInventoryInfo[]) => {
        const products: RestaurantProduct[][] = this.curProducts;
        let countInventoryVariations = variationList.length;
        for (const category of products) {
          if (!countInventoryVariations) break;
          for (const item of category) {
            if (!countInventoryVariations) break;
            for (const itemVariation of item.variations) {
              const variationInfo = variationList.find( variation => variation.catalog_object_id === itemVariation.id);
              if (variationInfo) {
                itemVariation.quantityRemaining = Number(variationInfo.quantity);
                countInventoryVariations--;
              }
            }
          }
        }
      }));
  }

  public checkIsCatalogUploaded(merchantId: string): Observable<boolean> {
    return this.restaurantService.checkIsCatalogUploaded(merchantId)
      .pipe(tap((result: any) => {
        this.isCatalogUploaded = result.uploaded;
      }));
  }

  public findLocationById(restaurants: Restaurant[], locationId: string): Restaurant {
    return restaurants?.find((restaurant) => restaurant.locationId === locationId);
  }

  public backToStorePage(fromPayment?: boolean, url?: string) {
    if (this.isGroup || (url && url.includes('group'))) {
      const groupUrl = this.routeService.getGroupUrl(url);
      this.updateGroupInfo = true;
      this.router.navigate([groupUrl], { queryParamsHandling: 'merge', queryParams: this.stringToParams(url)})
        .catch(error => console.error(error));
    } else if (this.group || this.groupIdForRedirect) {
      const groupUrl = this.routeService.createGroupUrl(this.group?.groupId || this.groupIdForRedirect);
      this.updateGroupInfo = true;
      this.router.navigate([groupUrl], { queryParamsHandling: 'merge', queryParams: this.stringToParams(url) })
        .catch(error => console.log(error));
    } else {
      const locationUrl = this.routeService.getLocationUrl(url);
      if (fromPayment) {
        this.router.navigate([locationUrl], {queryParamsHandling: 'merge' , queryParams: {fromPayment: true}})
          .catch(error => console.error(error));
      } else {
        this.router.navigate([locationUrl], { queryParamsHandling: 'merge', queryParams: this.stringToParams(url) })
          .catch(error => console.error(error));
      }
    }
  }

  public getRestaurantFulfillment(restaurant: Restaurant, fulfillmentParams?: any[]): any[] {
    const enabledFulfillments = [];
    const {isCurbsideEnabled, isTablesideEnabled, isPickupEnabled, isDeliveryEnabled, isXSideEnabled, handoffLocation, isFridgeEnabled} = restaurant.availableStatus;
    if (isPickupEnabled && this.checkFulfillmentParams('pickup', fulfillmentParams)) {
      enabledFulfillments.push('Pickup');
    }
    if (isCurbsideEnabled && this.checkFulfillmentParams('curbside', fulfillmentParams)) {
      enabledFulfillments.push('Curbside');
    }
    if (isTablesideEnabled && (restaurant.isAcceptingOrders || !restaurant.allowScheduledPickups) && this.checkFulfillmentParams('tableside', fulfillmentParams)) {
      enabledFulfillments.push('Tableside');
      if (restaurant.isTabsEnabled) {
        enabledFulfillments.push('Tab');
      }
    }
    if (isDeliveryEnabled && this.checkFulfillmentParams('delivery', fulfillmentParams)) {
      enabledFulfillments.push('Delivery');
    }
    if (isXSideEnabled && this.checkFulfillmentParams('xside', fulfillmentParams)) {
      if (handoffLocation) {
        enabledFulfillments.push('X-Side');
      }
    }
    if (isFridgeEnabled && this.checkFulfillmentParams('fridge', fulfillmentParams)) {
      enabledFulfillments.push('Fridge')
    }
    if (!isPickupEnabled && !isCurbsideEnabled && !isTablesideEnabled && !isDeliveryEnabled && !isXSideEnabled && !isFridgeEnabled) {
      enabledFulfillments.push('Pickup');
    }
    this.enabledFulfillments = enabledFulfillments;
    return enabledFulfillments;
  }

  public getUseInventoryItems(products: RestaurantProduct[]): string[] {
    const variationIdList = [];
    for (const product of products) {
      for (const variation of product.variations) {
        if (variation.trackInventory && variation.useInventory) {
          variationIdList.push(variation.id);
        }
      }
    }
    return variationIdList;
  }

  public getCategoryVariationIds(products: RestaurantProduct[]): string[] {
    const variationIdList = [];
    for (const product of products) {
      for (const variation of product.variations) {
        if (variation.trackInventory) {
          variationIdList.push(variation.id);
        }
      }
    }
    return variationIdList;
  }

  public filterItemsInCategory(products: RestaurantProduct[], isAlcoholHidden: boolean): RestaurantProduct[] {
    const filteredItems = [];
    this.curCategories$.subscribe(items => {
      for (const category of items) {
        if (this.showCategory(category, isAlcoholHidden)) {
          const filterItems = products.filter(item => this.showItem(item, isAlcoholHidden));
          if (filterItems.length > 0) {
            filteredItems.push(filterItems);
          }
        }
      }
    });
    return filteredItems;
  }

  public showItem(item: RestaurantProduct, isAlcoholHidden: boolean): boolean {
    if (isAlcoholHidden) {
      return !this.formatDataService.containsKeyword(item.info.name, ItemFilter.CONTAINS_ALCOHOL);
    }
    return true;
  }

  public showCategory(category: RestaurantCategory, isAlcoholHidden: boolean): boolean {
    if (isAlcoholHidden) {
      return !this.formatDataService.containsKeyword(category.name, ItemFilter.CONTAINS_ALCOHOL);
    }
    return true;
  }

  private checkFulfillmentParams(param: string, fulfillmentParams: any[]): boolean {
    if (fulfillmentParams != null && fulfillmentParams.length > 0) {
      return fulfillmentParams.includes(param);
    }
    return true;
  }

  private setCustomCss(customCss: string) {
    if (customCss && !this.isGroup) {
      const customData: CustomCss = JSON.parse(customCss);
      const {font, enlargeImages, itemColumns} = customData;
      if (font && font.length > 0) {
        this.formatStyleService.setGlobalFont(font);
      }
      this.itemColumns = itemColumns;
    }
  }

  private setCustomStyleService(restaurant: Restaurant) {
    this.formatStyleService.setGlobalBrandColor(restaurant.brandColor);
    this.formatStyleService.setGlobalBackgroundColor(restaurant.backgroundColor);
    this.formatStyleService.setGlobalBorderColor(restaurant.borderColor);
    this.formatStyleService.setGlobalPrimaryTextColor(restaurant.primaryTextColor);
    this.formatStyleService.setGlobalSecondaryTextColor(restaurant.secondaryTextColor);
  }

  private stringToParams(url: string): any {
    const obj = {};
    if (url && url.split('?')[1]) {
      const urlParam = url.split('?')[1];
      const urlSeparate = urlParam.split('&');
      urlSeparate.forEach(params => {
        const properties = params.split('=');
        obj[properties[0]] = properties[1];
      });
    }
    return obj;
  }

  public clearRestaurantData(): void {
    this._curCategories$.next([]);
    this._curProducts$.next([]);
    this._taxes$.next([]);
  }

  public getProductsByReward(rewardId: string, locationId: string, merchantId: string) {
    this.restaurantService.getProductsByReward(rewardId, locationId, merchantId).subscribe((products)=>{
      const currentProducts = this._rewardProducts.value;
      const existingIndex = currentProducts.findIndex(item => item.id === rewardId);

      if (existingIndex > -1) {
        currentProducts[existingIndex].items = products;
      } else {
        currentProducts.push({ id: rewardId, items: products });
      }
      this._rewardProducts.next([...currentProducts]);
    })
  }

}
